import React, { useState } from "react"

import {
	Dialog,
	Grid,
	IconButton,
	ThemeProvider
} from "@material-ui/core"
import { Close } from "@material-ui/icons"

import {
	Loading,
	Notification
} from "@/components"
import UpsellQuantityDialogIdleContent from "@/components/UpsellDialog/UpsellQuantityDialog/UpsellQuantityDialogIdleContent"
import UpsellQuantityDialogWaitingPaymentContent from "@/components/UpsellDialog/UpsellQuantityDialog/UpsellQuantityDialogWaitingPaymentContent"
import UpsellQuantityDialogSuccessContent from "@/components/UpsellDialog/UpsellQuantityDialog/UpsellQuantityDialogSuccessContent"
import UpsellQuantityDialogFailedContent from "@/components/UpsellDialog/UpsellQuantityDialog/UpsellQuantityDialogFailedContent"

import { renderComponent } from "@/utils/node"
import { whatsappLinks } from "@/utils/link"

import AccountService from "@/services/Account"
import AnalyticsService from "@/services/Analytics"
import ErrorHandlerService from "@/services/ErrorHandler"

import UpsellImage from "@/assets/images/voice-call-credits-rocket.png"

import useAsyncState from "@/hooks/useAsyncState"
import useDidMount from "@/hooks/useDidMount"
import useSocket from "@/hooks/useSocket"

import UpsellQuantityDialogSkeleton from "@/skeletons/UpsellQuantityDialogSkeleton"

import {
	PlanAddons,
	UpsellDetailsStatus,
	UpsellQuantityInfo,
	UpsellQuantityType
} from "@/protocols/planAddons"
import { UpsellDetails } from "@/protocols/upsell"

import theme from "@/styles/theme"
import useStyles from "@/components/UpsellDialog/UpsellQuantityDialog/styles"

import { ErrorType } from "@/hooks/useValidation"

type UpsellQuantityDialogProps = {
	type: UpsellQuantityType
	isUserReachSubscriptionLimit: boolean
	instanceId: number
}

type UpsellHandlerDialogType = {
	open: (props: UpsellQuantityDialogProps) => void
}

type UpsellCheckoutStateData = {
	content: React.ReactNode
}

const UpsellQuantityDialog: UpsellHandlerDialogType & React.FC<UpsellQuantityDialogProps> = (props) => {
	const {
		type,
		isUserReachSubscriptionLimit
	} = props

	const [isDialogOpen, setIsDialogOpen] = useState(true)
	const [createPlanAddonloading, setCreatePlanAddonloading] = useState(false)
	const [loading, setLoading] = useState(false)
	const [planAddons, setPlanAddons] = useState<PlanAddons<"quantity">>([])

	const [selectedUpsellPlanAddon, setSelectedUpsellPlanAddon] = useState<UpsellQuantityInfo>({} as UpsellQuantityInfo)

	const [upsellDetailsRef, setUpsellDetails] = useAsyncState<UpsellDetails>({
		upsellDetailsId: null,
		paymentLink: null,
		status: "idle",
		error: null
	})

	const classes = useStyles()
	const socket = useSocket()

	const handleGetPlanAddons = async () => {
		setLoading(true)

		try {
			const response = await AccountService.retrievelAllPlanAddonsByUpsellType<"quantity">(type, "quantity")

			setPlanAddons(response)

			const upsellQuantityTypeToDefaultQuantity: Record<UpsellQuantityType, number> = {
				user: 2,
				voice_call_minutes: 60
			}

			const defaultQuantity = upsellQuantityTypeToDefaultQuantity[type]
			const defaultSelectedAddon = response.find((addon) => addon.quantity === defaultQuantity) ?? response[0]

			if (defaultSelectedAddon) {
				setSelectedUpsellPlanAddon(defaultSelectedAddon)
			}
		} catch (error) {
			Notification.error({
				message: "Houve um erro ao buscar os adicionais!"
			})

			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleContactSalesViaWhatsApp = () => {
		AnalyticsService.capture("upsell_contact_sales_clicked")

		const upsellQuantityTypeToSalesWhatsappLink: Record<UpsellQuantityType, string> = {
			voice_call_minutes: whatsappLinks.sendMessageToAcquireVoiceCallMinutesModule,
			user: whatsappLinks.sendMessageToAcquireAditionalUserModule
		}

		const salesWhatsappLink = upsellQuantityTypeToSalesWhatsappLink[type] || whatsappLinks.sendMessageToAcquireModule

		window.open(salesWhatsappLink, "_blank")
	}

	const handleInitiateUpsellCheckout = async () => {
		AnalyticsService.capture("upsell_checkout_initiated")

		setCreatePlanAddonloading(true)

		try {
			const response = await AccountService.initiateUpsellCheckout({
				planAddonId: selectedUpsellPlanAddon.planAddonId,
				upsellType: type
			})

			setUpsellDetails(() => response)

			if (response.status === "awaiting-payment" && response.paymentLink) {
				window.open(response.paymentLink, "_blank")
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			handleContactSalesViaWhatsApp()
		}

		setCreatePlanAddonloading(false)
	}

	const handleCloseDialog = () => {
		setIsDialogOpen(false)
	}

	const handleDismissCheckoutError = () => {
		AnalyticsService.capture("upsell_checkout_failed")

		setUpsellDetails((prev) => ({
			...prev,
			status: "idle",
			error: null
		}))
	}

	const handleSuccessClose = () => {
		AnalyticsService.capture("upsell_checkout_succeeded")

		handleCloseDialog()
		window.location.reload()
	}

	const handleSaveDialog = async () => {
		await handleInitiateUpsellCheckout()
	}

	const handleSelectUpsellPlanAddon = (planAddonId: number) => {
		const upsell = planAddons?.find(upsell => upsell.planAddonId === planAddonId)

		if (upsell) {
			AnalyticsService.capture("upsell_quantity_selected")
			setSelectedUpsellPlanAddon(upsell)
		}
	}

	const restoreStateFromLatestAttempt = async () => {
		const latestAttempt = await AccountService.getLatestUpsellAttempt({
			upsellType: type
		})

		if (!latestAttempt.upsellDetailsId) {
			return
		}

		const restorableStatuses: UpsellDetailsStatus[] = ["started", "awaiting-payment"]
		const isLatestAttemptStillInProgress = restorableStatuses.includes(latestAttempt.status)

		if (!isLatestAttemptStillInProgress) {
			return
		}

		setUpsellDetails(() => latestAttempt)
	}

	useDidMount(() => {
		handleGetPlanAddons()

		socket.onUpsellStatusUpdated((payload) => {
			const currentUpsellDetailsId = upsellDetailsRef.current.upsellDetailsId

			if (currentUpsellDetailsId === null || payload.upsellDetailsId !== currentUpsellDetailsId) {
				return
			}

			setUpsellDetails((prev) => ({
				...prev,
				status: payload.status as UpsellDetailsStatus,
				error: payload.error
			}))
		})

		restoreStateFromLatestAttempt()
	})

	const getUpsellCheckoutStateData = () => {
		const upsellCheckoutStatusToData: Record<UpsellDetailsStatus, UpsellCheckoutStateData> = {
			idle: {
				content: (
					<UpsellQuantityDialogIdleContent
						upsellDetails={upsellDetailsRef.current}
						isUserReachSubscriptionLimit={isUserReachSubscriptionLimit}
						upsellQuantityType={type}
						planAddons={planAddons}
						selectedUpsellPlanAddon={selectedUpsellPlanAddon}
						isCreatePlanAddonLoading={createPlanAddonloading}
						onActivateClick={handleSaveDialog}
						onCancelClick={handleCloseDialog}
						onSelectPlanAddon={handleSelectUpsellPlanAddon}
					/>
				)
			},
			started: {
				content: (
					<UpsellQuantityDialogWaitingPaymentContent
						upsellDetails={upsellDetailsRef.current}
						upsellQuantityType={type}
						selectedUpsellPlanAddon={selectedUpsellPlanAddon}
					/>
				)
			},
			"awaiting-payment": {
				content: (
					<UpsellQuantityDialogWaitingPaymentContent
						upsellDetails={upsellDetailsRef.current}
						upsellQuantityType={type}
						selectedUpsellPlanAddon={selectedUpsellPlanAddon}
					/>
				)
			},
			activated: {
				content: (
					<UpsellQuantityDialogSuccessContent
						upsellDetails={upsellDetailsRef.current}
						upsellQuantityInfo={selectedUpsellPlanAddon}
						upsellQuantityType={type}
						onCloseClick={handleSuccessClose}
					/>
				)
			},
			paid: {
				content: (
					<UpsellQuantityDialogSuccessContent
						upsellDetails={upsellDetailsRef.current}
						upsellQuantityInfo={selectedUpsellPlanAddon}
						upsellQuantityType={type}
						onCloseClick={handleSuccessClose}
					/>
				)
			},
			failed: {
				content: (
					<UpsellQuantityDialogFailedContent
						upsellDetails={upsellDetailsRef.current}
						onRetryClick={handleDismissCheckoutError}
						onContactSupportClick={handleContactSalesViaWhatsApp}
					/>
				)
			}
		}

		return upsellCheckoutStatusToData[upsellDetailsRef.current.status as UpsellDetailsStatus]
	}

	const upsellCheckoutStateData = getUpsellCheckoutStateData()

	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={isDialogOpen}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					className: classes.fullDialog
				}}
			>
				<Loading loading={loading} customLoadingElement={<UpsellQuantityDialogSkeleton />}>
					<Grid
						container
						className={classes.fullDialog}
					>
						<Grid
							item
							xs={8}
						>
							{upsellCheckoutStateData.content}
						</Grid>
						<Grid
							xs={4}
							className={classes.imageContainer}
							item
						>
							<img
								alt="Foguete"
								className={classes.upsellImage}
								src={UpsellImage}
							/>

							<IconButton className={classes.closeButton} onClick={handleCloseDialog}>
								<Close />
							</IconButton>
						</Grid>
					</Grid>
				</Loading>
			</Dialog>
		</ThemeProvider>
	)
}

UpsellQuantityDialog.open = (props: UpsellQuantityDialogProps) => {
	renderComponent(
		props.type,
		<UpsellQuantityDialog
			{...props}
		/>
	)
}

export default UpsellQuantityDialog
